/**
 * 外部メディアダウンロード共通フック
 *
 * 動画 / 音楽ダウンロードの SSE 進捗管理・Blob取得・IndexedDB保存ロジックを統合。
 * 各メディア種別は config を渡して薄いラッパーフックとして利用する。
 */
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { ExternalDownloadCompleted, ExternalDownloadEvent } from '@/api/type'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DownloadState = {
  isDownloading: boolean
  progress: number
  status: string
  message: string
  downloadedBytes?: number
  totalBytes?: number
  speed?: number
  eta?: number
  error?: string
}

/** SSE ストリームを開始するコールバック */
export type StartStreamFn = (
  onMessage: (data: unknown) => void,
  onError: (error: Error) => void,
  signal: AbortSignal,
) => void

/** 完了時の保存パラメータ */
export type SaveParams<TCompleted extends ExternalDownloadCompleted> = {
  id: string
  mediaBlob: Blob
  thumbnailBlob: Blob | null
  data: TCompleted
  url: string
}

/** メディア種別ごとのコンフィグ */
export type ExternalDownloadConfig<TCompleted extends ExternalDownloadCompleted> = {
  /** 表示用ラベル ("動画" | "音声") */
  mediaLabel: string
  /** ファイルをBlobで取得 */
  fetchFile: (filePath: string, onProgress?: (progress: number, loaded: number, total: number) => void) => Promise<Blob>
  /** サムネイルをBlobで取得 */
  fetchThumbnail: (thumbnailPath: string) => Promise<Blob>
  /** IndexedDB への保存処理 */
  saveToDb: (params: SaveParams<TCompleted>) => Promise<void>
}

const INITIAL_STATE: DownloadState = {
  isDownloading: false,
  progress: 0,
  status: 'idle',
  message: '',
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useExternalDownload = <TCompleted extends ExternalDownloadCompleted>(
  config: ExternalDownloadConfig<TCompleted>,
) => {
  const [downloadState, setDownloadState] = useState<DownloadState>(INITIAL_STATE)

  const abortControllerRef = useRef<AbortController | null>(null)
  const isCompletedRef = useRef(false)
  const isDownloadingRef = useRef(false)

  /**
   * ダウンロードを開始する
   * @param url ダウンロード対象URL
   * @param startStream SSEストリーム開始関数（メディア種別ごとに異なるパラメータを束縛済み）
   */
  const startDownload = useCallback(
    (url: string, startStream: StartStreamFn) => {
      if (!url.trim()) return
      if (isDownloadingRef.current) {
        console.warn('Download already in progress, ignoring duplicate call')
        return
      }

      isDownloadingRef.current = true

      // 前回のリクエストをキャンセル
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()
      isCompletedRef.current = false

      setDownloadState({
        isDownloading: true,
        progress: 0,
        status: 'connecting',
        message: 'サーバーに接続中...',
      })

      const { mediaLabel, fetchFile, fetchThumbnail, saveToDb } = config

      // --- SSE メッセージハンドラ ---
      const handleMessage = async (rawData: unknown) => {
        try {
          const data = rawData as ExternalDownloadEvent<TCompleted>

          // エラーイベント
          if ('error' in data) {
            isDownloadingRef.current = false
            setDownloadState({
              isDownloading: false,
              progress: 0,
              status: 'error',
              message: data.error,
              error: data.error,
            })
            toast.error(data.error, { description: data.suggestion })
            return
          }

          switch (data.status) {
            case 'started':
              setDownloadState({
                isDownloading: true,
                progress: data.progress,
                status: data.status,
                message: data.message,
              })
              toast.info(`${mediaLabel}のダウンロードを開始しました`)
              break

            case 'downloading':
              setDownloadState({
                isDownloading: true,
                progress: data.progress,
                status: data.status,
                message: `ダウンロード中... ${data.progress}%`,
                downloadedBytes: data.downloaded_bytes,
                totalBytes: data.total_bytes,
                speed: data.speed ?? undefined,
                eta: data.eta ?? undefined,
              })
              break

            case 'processing':
              setDownloadState({
                isDownloading: true,
                progress: data.progress,
                status: data.status,
                message: data.message,
              })
              break

            case 'completed': {
              if (isCompletedRef.current) return
              isCompletedRef.current = true

              try {
                // file_path から media_id を抽出: /external-{type}/file/{id}/{endpoint}
                const segments = data.file_path.split('/').filter(Boolean)
                const id = segments.at(2)
                if (!id) {
                  throw new Error(`Invalid file_path: ${data.file_path}`)
                }

                setDownloadState({
                  isDownloading: true,
                  progress: 0,
                  status: 'fetching',
                  message: `${mediaLabel}ファイルを取得中...`,
                })

                const mediaBlob = await fetchFile(data.file_path, (progress, loaded, total) => {
                  setDownloadState({
                    isDownloading: true,
                    progress,
                    status: 'fetching',
                    message: `${mediaLabel}ファイルを取得中... ${progress}%`,
                    downloadedBytes: loaded,
                    totalBytes: total,
                  })
                })

                // サムネイル取得
                let thumbnailBlob: Blob | null = null
                if (data.thumbnail_path) {
                  try {
                    setDownloadState({
                      isDownloading: true,
                      progress: 100,
                      status: 'fetching',
                      message: 'サムネイルを取得中...',
                    })
                    thumbnailBlob = await fetchThumbnail(data.thumbnail_path)
                  } catch (error) {
                    console.error('Failed to fetch thumbnail:', error)
                  }
                }

                // 保存
                setDownloadState({
                  isDownloading: true,
                  progress: 100,
                  status: 'saving',
                  message: '保存中...',
                })

                await saveToDb({
                  id,
                  mediaBlob,
                  thumbnailBlob,
                  data: data as TCompleted,
                  url,
                })
              } catch (error) {
                console.error(`Failed to save ${mediaLabel} to IndexedDB:`, error)
                toast.error(`${mediaLabel}の保存に失敗しました`)
                isDownloadingRef.current = false
                return
              }

              setDownloadState({
                isDownloading: false,
                progress: 100,
                status: 'completed',
                message: '保存が完了しました',
              })
              isDownloadingRef.current = false
              break
            }

            default:
              console.warn('Unknown status:', data)
          }
        } catch (error) {
          console.error('Failed to parse SSE message:', error)
        }
      }

      // --- エラーハンドラ ---
      const handleError = (error: Error) => {
        if (isCompletedRef.current) return
        isDownloadingRef.current = false
        setDownloadState({
          isDownloading: false,
          progress: 0,
          status: 'error',
          message: 'ダウンロード中にエラーが発生しました',
          error: error.message,
        })
        toast.error('ダウンロード中にエラーが発生しました')
      }

      // SSEストリーム開始
      startStream(handleMessage, handleError, abortControllerRef.current.signal)
    },
    [config],
  )

  const cancelDownload = useCallback(() => {
    if (!abortControllerRef.current) return
    abortControllerRef.current.abort()
    abortControllerRef.current = null
    isDownloadingRef.current = false
    setDownloadState({
      isDownloading: false,
      progress: 0,
      status: 'cancelled',
      message: 'ダウンロードをキャンセルしました',
    })
  }, [])

  return { downloadState, startDownload, cancelDownload }
}
